import { Helmet } from 'react-helmet-async';

// Business Schema Data
const businessData = {
  name: "Most Wanted Design",
  description: "Studio de design interior Cluj specializat în amenajări HoReCa, cafenele, restaurante și spații comerciale. Proiecte personalizate de la concept la implementare.",
  url: "https://www.mostwanted.ro",
  logo: "https://www.mostwanted.ro/wp-content/uploads/2023/05/logo-mostwanted-400x121.png",
  image: "https://www.mostwanted.ro/wp-content/uploads/2023/06/cover-image-chi-cafe-floresti-design-by-most-wanted-design.jpg",
  phone: "+40740814449",
  email: "contact@mostwanted.ro",
  address: {
    street: "Bulevardul 21 Decembrie 1989, nr. 37",
    city: "Cluj-Napoca",
    region: "Cluj",
    postalCode: "400124",
    country: "RO"
  },
  geo: {
    latitude: "46.7712",
    longitude: "23.6236"
  },
  priceRange: "$$",
  openingHours: "Mo-Fr 10:00-17:00",
  socialLinks: {
    facebook: "https://www.facebook.com/mostwanted.ro",
    instagram: "https://www.instagram.com/mostwanted.design/"
  },
  serviceArea: ["Cluj-Napoca", "Cluj County", "Romania", "Europe"],
  services: [
    "Design interior cafenea",
    "Design interior restaurant", 
    "Design interior hotel",
    "Design interior birouri",
    "Design interior apartamente",
    "Randari 3D",
    "Management de proiect"
  ]
};

// SEO Configuration for each page
export const seoConfig = {
  home: {
    title: "Design Interior Cluj | Amenajări Cafenele & Restaurante | Most Wanted Design",
    description: "Studio de design interior Cluj specializat în amenajări HoReCa. Design interior cafenea, restaurant, hotel. Proiecte personalizate, randări 3D. ☎ +40740814449",
    keywords: "design interior cluj, design interior cafenea, interior design cluj, designer interior cluj, design restaurant, designer de interior, design interior pret, amenajari cafenele",
    canonical: "https://www.mostwanted.ro/",
    ogType: "website"
  },
  about: {
    title: "Despre Noi | Echipa Most Wanted Design Cluj | Designer Interior",
    description: "Echipă de designeri interiori Cluj cu 20+ ani experiență. Specializați în design cafenele, restaurante, hoteluri. Peste 150 proiecte finalizate. Consultație gratuită!",
    keywords: "designer interior cluj, echipa design interior, studio design cluj, designer de interior romania, firma design interior",
    canonical: "https://www.mostwanted.ro/about/",
    ogType: "website"
  },
  portfolio: {
    title: "Portofoliu Design Interior | Cafenele, Restaurante, Hoteluri | Most Wanted",
    description: "Galerie proiecte design interior: cafenele moderne, restaurante elegante, hoteluri de lux, apartamente Airbnb. Vezi transformările noastre spectaculoase!",
    keywords: "portofoliu design interior, proiecte cafenea, design restaurant realizat, amenajari interioare exemple, design interior preturi",
    canonical: "https://www.mostwanted.ro/portfolio/",
    ogType: "website"
  }
};

// LocalBusiness Schema
const getLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": businessData.url,
  "name": businessData.name,
  "description": businessData.description,
  "url": businessData.url,
  "logo": businessData.logo,
  "image": businessData.image,
  "telephone": businessData.phone,
  "email": businessData.email,
  "priceRange": businessData.priceRange,
  "address": {
    "@type": "PostalAddress",
    "streetAddress": businessData.address.street,
    "addressLocality": businessData.address.city,
    "addressRegion": businessData.address.region,
    "postalCode": businessData.address.postalCode,
    "addressCountry": businessData.address.country
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": businessData.geo.latitude,
    "longitude": businessData.geo.longitude
  },
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    "opens": "10:00",
    "closes": "17:00"
  },
  "sameAs": [
    businessData.socialLinks.facebook,
    businessData.socialLinks.instagram
  ],
  "areaServed": businessData.serviceArea.map(area => ({
    "@type": "Place",
    "name": area
  })),
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Servicii Design Interior",
    "itemListElement": businessData.services.map((service, index) => ({
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": service
      }
    }))
  }
});

// Organization Schema
const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": businessData.name,
  "url": businessData.url,
  "logo": businessData.logo,
  "description": businessData.description,
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": businessData.phone,
    "contactType": "customer service",
    "email": businessData.email,
    "areaServed": "RO",
    "availableLanguage": ["Romanian", "English"]
  },
  "sameAs": [
    businessData.socialLinks.facebook,
    businessData.socialLinks.instagram
  ]
});

// BreadcrumbList Schema
const getBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

// WebPage Schema
const getWebPageSchema = (page) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": page.title,
  "description": page.description,
  "url": page.canonical,
  "isPartOf": {
    "@type": "WebSite",
    "name": businessData.name,
    "url": businessData.url
  },
  "about": {
    "@type": "Organization",
    "name": businessData.name
  }
});

// SEO Component
export const SEO = ({ page, customTitle, customDescription, customImage, breadcrumbs }) => {
  const config = seoConfig[page] || seoConfig.home;
  const title = customTitle || config.title;
  const description = customDescription || config.description;
  const image = customImage || businessData.image;
  const canonical = config.canonical;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={config.keywords} />
      <meta name="author" content={businessData.name} />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="Romanian" />
      <meta name="revisit-after" content="7 days" />
      <link rel="canonical" href={canonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={config.ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={businessData.name} />
      <meta property="og:locale" content="ro_RO" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Geo Tags */}
      <meta name="geo.region" content="RO-CJ" />
      <meta name="geo.placename" content="Cluj-Napoca" />
      <meta name="geo.position" content={`${businessData.geo.latitude};${businessData.geo.longitude}`} />
      <meta name="ICBM" content={`${businessData.geo.latitude}, ${businessData.geo.longitude}`} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(getLocalBusinessSchema())}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(getOrganizationSchema())}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(getWebPageSchema(config))}
      </script>
      {breadcrumbs && (
        <script type="application/ld+json">
          {JSON.stringify(getBreadcrumbSchema(breadcrumbs))}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
